import type { Cvss4Metrics } from './cvss-types';
import { calculateAeBaseScore } from './cvss-ae-calculator';

export const generateCvss4VectorString = (metrics: Cvss4Metrics): string => {
  return [
    'CVSS:4.0',
    `AV:${metrics.attackVector}`,
    `AC:${metrics.attackComplexity}`,
    `AT:${metrics.attackRequirements}`,
    `PR:${metrics.privilegesRequired}`,
    `UI:${metrics.userInteraction}`,
    `VC:${metrics.vulnConfidentiality}`,
    `VI:${metrics.vulnIntegrity}`,
    `VA:${metrics.vulnAvailability}`,
    `SC:${metrics.subConfidentiality}`,
    `SI:${metrics.subIntegrity}`,
    `SA:${metrics.subAvailability}`,
  ].join('/');
};

export const parseCvss4VectorString = (
  vector: string,
): Partial<Cvss4Metrics> => {
  if (typeof vector !== 'string' || !vector.startsWith('CVSS:4.0')) {
    return {};
  }

  const parts = vector.split('/');
  const parsed: Partial<Cvss4Metrics> = {};

  parts.forEach((part) => {
    const [key, value] = part.split(':');
    switch (key) {
      case 'AV':
        parsed.attackVector = value;
        break;
      case 'AC':
        parsed.attackComplexity = value;
        break;
      case 'AT':
        parsed.attackRequirements = value;
        break;
      case 'PR':
        parsed.privilegesRequired = value;
        break;
      case 'UI':
        parsed.userInteraction = value;
        break;
      case 'VC':
        parsed.vulnConfidentiality = value;
        break;
      case 'VI':
        parsed.vulnIntegrity = value;
        break;
      case 'VA':
        parsed.vulnAvailability = value;
        break;
      case 'SC':
        parsed.subConfidentiality = value;
        break;
      case 'SI':
        parsed.subIntegrity = value;
        break;
      case 'SA':
        parsed.subAvailability = value;
        break;
    }
  });

  return parsed;
};

export const areAllCvss4MetricsSelected = (metrics: Cvss4Metrics): boolean => {
  return !!(
    metrics.attackVector &&
    metrics.attackComplexity &&
    metrics.attackRequirements &&
    metrics.privilegesRequired &&
    metrics.userInteraction &&
    metrics.vulnConfidentiality &&
    metrics.vulnIntegrity &&
    metrics.vulnAvailability &&
    metrics.subConfidentiality &&
    metrics.subIntegrity &&
    metrics.subAvailability
  );
};

export const getInitialCvss4Metrics = (): Cvss4Metrics => ({
  attackVector: '',
  attackComplexity: '',
  attackRequirements: '',
  privilegesRequired: '',
  userInteraction: '',
  vulnConfidentiality: '',
  vulnIntegrity: '',
  vulnAvailability: '',
  subConfidentiality: '',
  subIntegrity: '',
  subAvailability: '',
});

export const calculateCvss4Score = (metrics: Cvss4Metrics): number => {
  if (!areAllCvss4MetricsSelected(metrics)) {
    return 0;
  }
  return calculateAeBaseScore(generateCvss4VectorString(metrics)) ?? 0;
};

export const calculateCvss4BaseScoreFromVector = (
  vector: string,
): number | null => {
  if (!vector.startsWith('CVSS:4.0')) {
    return null;
  }
  return calculateAeBaseScore(vector);
};
